import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Chip,
  Button,
  Checkbox,
  Divider,
  TextField,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  ListItemIcon,
  ListItemText,
  Menu,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import { ArrowDropDown, Search, GetApp } from "@mui/icons-material";
import { htmlContentStyles, sectionTitleStyles, tableContainerStyles } from "../styles";
import { TeamStatisticsFocusFilter } from "../team-statistics-focus-filter";

const convertToPDF = (
  data: RowObject[],
  searchTerm?: string,
  sortConfig?: SortConfig
): string => {
  if (data.length === 0) return "";

  const headers = Object.keys(data[0]);

  let html = `
    <html>
      <head>
        <title>Statistics Report</title>
        <style>
          body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            font-size: 12px;
            line-height: 1.4;
          }
          .header {
            margin-bottom: 20px;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
          }
          .info {
            background-color: #f8f9fa;
            padding: 10px;
            border-radius: 4px;
            margin-bottom: 15px;
            font-size: 11px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse; 
            margin-top: 10px;
            font-size: 10px;
          }
          th, td { 
            border: 1px solid #ddd; 
            padding: 6px 4px; 
            text-align: left;
            word-wrap: break-word;
          }
          th { 
            background-color: #f2f2f2; 
            font-weight: bold;
            position: sticky;
            top: 0;
          }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .summary {
            margin-top: 15px;
            font-size: 11px;
            color: #666;
          }
          @media print {
            body { margin: 10px; }
            table { font-size: 9px; }
            th, td { padding: 4px 2px; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Statistics Report</h1>
          <p>Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</p>
        </div>
        
        <div class="info">
          <strong>Export Information:</strong><br>
          • Total Records: ${data.length}<br>
          ${searchTerm ? `• Filtered by: "${searchTerm}"<br>` : ""}
          ${sortConfig ? `• Sorted by: ${sortConfig.key} (${sortConfig.direction === "asc" ? "Ascending" : "Descending"})<br>` : ""}
          • Columns: ${headers.length}
        </div>
        
        <table>
          <thead>
            <tr>
              ${headers.map((header) => `<th>${header}</th>`).join("")}
            </tr>
          </thead>
          <tbody>
            ${data
              .map(
                (row) => `
              <tr>
                ${headers.map((header) => `<td>${(row[header] || "").toString()}</td>`).join("")}
              </tr>
            `
              )
              .join("")}
          </tbody>
        </table>
        
        <div class="summary">
          <p><strong>Summary:</strong> This report contains ${data.length} records with ${headers.length} columns each.</p>
        </div>
      </body>
    </html>
  `;

  return html;
};

const downloadPDF = (html: string, filename: string) => {
  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();

    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        setTimeout(() => {
          printWindow.close();
        }, 1000);
      }, 500);
    };
  }
};

interface HtmlContentProps {
  html: string | undefined;
  title?: string;
}

interface SortConfig {
  key: string;
  direction: "asc" | "desc";
}

interface RowObject {
  [key: string]: string;
}

type ParsedTable = { columns: string[]; rows: string[][] };

function findFocusColumnIndex(columns: string[]): number {
  const normalized = columns.map((c) => c.trim().toLowerCase());
  const exact = normalized.findIndex((c) => c === "focus");
  if (exact !== -1) return exact;

  const preferred = normalized.findIndex(
    (c) =>
      c.includes("focus area") ||
      c.includes("focus") ||
      c.includes("product focus") ||
      c.includes("selected focus")
  );
  return preferred;
}

function parseFirstHtmlTable(html: string): ParsedTable | null {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const table = doc.querySelector("table");
    if (!table) return null;

    const headerCells = Array.from(table.querySelectorAll("thead th"));
    const columnsFromTh = headerCells
      .map((th) => th.textContent?.trim() || "")
      .filter(Boolean);

    const bodyRows = Array.from(table.querySelectorAll("tbody tr"));
    const fallbackRows =
      bodyRows.length > 0
        ? bodyRows
        : Array.from(table.querySelectorAll("tr")).slice(1);

    const rows = fallbackRows.map((tr) =>
      Array.from(tr.querySelectorAll("td, th")).map(
        (cell) => cell.textContent?.trim() || ""
      )
    );

    const columns =
      columnsFromTh.length > 0
        ? columnsFromTh
        : Array.from(
            { length: Math.max(0, rows[0]?.length ?? 0) },
            (_, i) => `Column ${i + 1}`
          );

    const normalizedRows = rows.map((r) => {
      if (r.length === columns.length) return r;
      const padded = [...r];
      while (padded.length < columns.length) padded.push("");
      return padded.slice(0, columns.length);
    });

    return { columns, rows: normalizedRows };
  } catch {
    return null;
  }
}

function compareCell(aRaw: string, bRaw: string): number {
  const aNum = Number(aRaw);
  const bNum = Number(bRaw);
  const aNumOk = Number.isFinite(aNum) && aRaw.trim() !== "";
  const bNumOk = Number.isFinite(bNum) && bRaw.trim() !== "";

  if (aNumOk && bNumOk) return aNum - bNum;
  return aRaw.localeCompare(bRaw);
}

function findScrollableParent(element: HTMLElement | null): HTMLElement | Window {
  if (!element) return window;
  
  let parent = element.parentElement;
  while (parent) {
    const style = window.getComputedStyle(parent);
    const overflowY = style.overflowY;
    const maxHeight = style.maxHeight;
    
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      (maxHeight !== 'none' || parent.scrollHeight > parent.clientHeight)
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return window;
}

export const HtmlContent: React.FC<HtmlContentProps> = ({
  html: htmlProp,
  title,
}) => {
  const [visibleColumns, setVisibleColumns] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [focusValue, setFocusValue] = useState<string>("");
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const [columnsAnchorEl, setColumnsAnchorEl] = useState<null | HTMLElement>(
    null
  );
  const paginationRef = useRef<HTMLDivElement>(null);
  const previousRowsPerPageRef = useRef<number>(rowsPerPage);
  const previousPageRef = useRef<number>(page);

  // DEV-only helper: allow UI testing without backend team data.
  // Disabled to show ONLY real backend data:
  const ENABLE_MOCK_TABLE = false;
  const effectiveHtml = useMemo(() => {
    if (!htmlProp) return "";
    const hasNoTeamMessage =
      htmlProp.includes("No team to display") ||
      htmlProp.includes("no team to display");
    if (!ENABLE_MOCK_TABLE || !hasNoTeamMessage) return htmlProp;
    return `
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Score</th>
            <th>Focus</th>
            <th>Competency</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          <tr><td>Test User 1</td><td>4.5</td><td>Kisqali</td><td>Sales</td><td>Active</td></tr>
          <tr><td>Test User 2</td><td>4.2</td><td>Kisqali</td><td>Sales</td><td>Active</td></tr>
          <tr><td>Test User 3</td><td>3.8</td><td>Kisqali</td><td>Sales</td><td>Active</td></tr>
          <tr><td>Test User 4</td><td>3.8</td><td>Kisqali</td><td>Sales</td><td>Active</td></tr>
          <tr><td>Test User 5</td><td>3.8</td><td>Kisqali</td><td>Sales</td><td>Active</td></tr>
          <tr><td>Test User 6</td><td>3.8</td><td>Kisqali</td><td>Sales</td><td>Active</td></tr>
        </tbody>
      </table>
    `;
  }, [ENABLE_MOCK_TABLE, htmlProp]);

  const parsed = useMemo(() => {
    if (!effectiveHtml || effectiveHtml.trim() === "") return null;
    return parseFirstHtmlTable(effectiveHtml);
  }, [effectiveHtml]);

  const columns = useMemo(() => parsed?.columns ?? [], [parsed]);
  const rows = useMemo(() => parsed?.rows ?? [], [parsed]);

  const focusColumnIndex = useMemo(() => {
    if (columns.length === 0) return -1;
    return findFocusColumnIndex(columns);
  }, [columns]);

  const focusOptions = useMemo(() => {
    if (focusColumnIndex < 0) return [];
    const values = new Set<string>();
    rows.forEach((r) => {
      const v = (r[focusColumnIndex] ?? "").trim();
      if (v) values.add(v);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b));
  }, [focusColumnIndex, rows]);

  useEffect(() => {
    if (columns.length === 0) {
      setVisibleColumns(new Set());
      return;
    }
    setVisibleColumns(new Set(columns.map((_, idx) => idx)));
  }, [columns]);

  useEffect(() => {
    setPage(0);
  }, [searchTerm, sortConfig, rowsPerPage, focusValue]);

  useEffect(() => {
    if (focusColumnIndex < 0 && focusValue) setFocusValue("");
  }, [focusColumnIndex, focusValue]);

  useEffect(() => {
    const shouldRestore = 
      (previousRowsPerPageRef.current !== rowsPerPage) || 
      (previousPageRef.current !== page);
    
    if (shouldRestore && paginationRef.current) {
      const paginationElement = paginationRef.current;
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const scrollableContainer = findScrollableParent(paginationElement);
          const currentRect = paginationElement.getBoundingClientRect();
          
          if (scrollableContainer === window) {
            const viewportHeight = window.innerHeight;
            const elementBottom = currentRect.bottom;
            const scrollY = window.scrollY || window.pageYOffset;
            const targetScrollY = scrollY + elementBottom - viewportHeight;
            
            window.scrollTo({
              top: targetScrollY,
              behavior: 'instant' as ScrollBehavior
            });
          } else {
            const container = scrollableContainer as HTMLElement;
            const containerRect = container.getBoundingClientRect();
            const containerHeight = containerRect.height;
            const elementTop = currentRect.top;
            
            const elementScrollTop = elementTop - containerRect.top + container.scrollTop;
            const targetScrollTop = elementScrollTop - containerHeight + currentRect.height;
            
            container.scrollTo({
              top: Math.max(0, targetScrollTop),
              behavior: 'instant'
            });
          }
        });
      });
      
      previousRowsPerPageRef.current = rowsPerPage;
      previousPageRef.current = page;
    }
  }, [rowsPerPage, page]);

  const handleSort = useCallback((key: string) => {
    setSortConfig((prev) => {
      if (!prev || prev.key !== key) return { key, direction: "asc" };
      return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
    });
  }, []);

  const filteredRows = useMemo(() => {
    let base = rows;
    if (focusColumnIndex >= 0 && focusValue) {
      const selected = focusValue.toLowerCase();
      base = base.filter(
        (r) => (r[focusColumnIndex] ?? "").toLowerCase() === selected
      );
    }
    if (!searchTerm) return base;
    const q = searchTerm.toLowerCase();
    return base.filter((r) =>
      r.some((cell) => (cell || "").toLowerCase().includes(q))
    );
  }, [rows, searchTerm, focusColumnIndex, focusValue]);

  const sortedRows = useMemo(() => {
    if (!sortConfig) return filteredRows;
    const idx = columns.indexOf(sortConfig.key);
    if (idx === -1) return filteredRows;
    const direction = sortConfig.direction === "asc" ? 1 : -1;

    const copy = [...filteredRows];
    copy.sort((a, b) => direction * compareCell(a[idx] ?? "", b[idx] ?? ""));
    return copy;
  }, [columns, filteredRows, sortConfig]);

  const totalRowsCount = rows.length;
  const filteredRowsCount = sortedRows.length;

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  const visibleColumnIndexes = useMemo(() => {
    const idxs = columns.map((_, i) => i).filter((i) => visibleColumns.has(i));
    return idxs;
  }, [columns, visibleColumns]);

  const visibleColumnsCount = visibleColumnIndexes.length;

  const resetAllColumns = () => {
    setVisibleColumns(new Set(columns.map((_, i) => i)));
  };

  const hideAllColumns = () => {
    setVisibleColumns(new Set());
  };

  const toggleColumn = (idx: number) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleOpenColumnsMenu = (event: React.MouseEvent<HTMLElement>) => {
    setColumnsAnchorEl(event.currentTarget);
  };

  const handleCloseColumnsMenu = () => {
    setColumnsAnchorEl(null);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    previousPageRef.current = page;
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    previousRowsPerPageRef.current = rowsPerPage;
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchorEl(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportAnchorEl(null);
  };

  const exportToPDF = () => {
    const data: RowObject[] = sortedRows.map((r) => {
      const obj: RowObject = {};
      visibleColumnIndexes.forEach((idx) => {
        obj[columns[idx]] = r[idx] ?? "";
      });
      return obj;
    });
    const pdfData = convertToPDF(
      data,
      searchTerm || undefined,
      sortConfig || undefined
    );
    downloadPDF(pdfData, "statistics.pdf");
    handleExportClose();
  };

  const hasTable = columns.length > 0;

  return (
    <Box sx={tableContainerStyles}>
      {title && (
        <Typography variant="h6" sx={sectionTitleStyles}>
          {title}
        </Typography>
      )}
      {/* Controls are always mounted; disabled when there is no table. */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mb: 2,
          opacity: hasTable ? 1 : 0.6,
          pointerEvents: hasTable ? "auto" : "none",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 2,
              flexGrow: 1,
              flexWrap: "wrap",
            }}
          >
            <TextField
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ maxWidth: 400 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
            {focusColumnIndex >= 0 && (
              <TeamStatisticsFocusFilter
                label="Focus"
                value={focusValue}
                onChange={setFocusValue}
                options={focusOptions}
                disabled={!hasTable}
              />
            )}
            <Chip
              label={`Found: ${filteredRowsCount} of ${totalRowsCount}`}
              color={filteredRowsCount < totalRowsCount ? "success" : "default"}
              variant="outlined"
              size="small"
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Export data">
              <span>
                <IconButton
                  onClick={handleExportClick}
                  size="small"
                  disabled={!hasTable || visibleColumnsCount === 0}
                >
                  <GetApp />
                </IconButton>
              </span>
            </Tooltip>
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={handleExportClose}
            >
              <MenuItem onClick={exportToPDF}>Export as PDF</MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Button
            size="small"
            onClick={handleOpenColumnsMenu}
            disabled={!hasTable}
            variant="outlined"
            endIcon={<ArrowDropDown />}
          >
            {`Columns (${visibleColumnsCount}/${columns.length})`}
          </Button>
          <Menu
            anchorEl={columnsAnchorEl}
            open={Boolean(columnsAnchorEl)}
            onClose={handleCloseColumnsMenu}
          >
            <MenuItem
              onClick={() => {
                resetAllColumns();
              }}
              disabled={!hasTable || visibleColumnsCount === columns.length}
            >
              Select all
            </MenuItem>
            <MenuItem
              onClick={() => {
                hideAllColumns();
              }}
              disabled={!hasTable || visibleColumnsCount === 0}
            >
              Clear
            </MenuItem>
            <Divider />
            {columns.map((col, idx) => (
              <MenuItem key={col} onClick={() => toggleColumn(idx)}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <Checkbox
                    edge="start"
                    checked={visibleColumns.has(idx)}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary={col} />
              </MenuItem>
            ))}
          </Menu>
          <Button
            size="small"
            onClick={resetAllColumns}
            disabled={!hasTable || visibleColumnsCount === columns.length}
            variant="outlined"
          >
            Show All
          </Button>
          <Button
            size="small"
            onClick={hideAllColumns}
            disabled={!hasTable || visibleColumnsCount === 0}
            variant="outlined"
          >
            Hide All
          </Button>

          {sortConfig && (
            <Chip
              label={`Sorted by: ${sortConfig.key} (${sortConfig.direction})`}
              color="secondary"
              variant="outlined"
              size="small"
              onDelete={() => setSortConfig(null)}
            />
          )}
        </Box>
      </Box>

      <Box sx={htmlContentStyles}>
        {!hasTable ? (
          <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
            No team to display
          </Box>
        ) : visibleColumnsCount === 0 ? (
          <Box sx={{ p: 2, textAlign: "center", color: "text.secondary" }}>
            No columns selected
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {visibleColumnIndexes.map((colIdx) => {
                    const col = columns[colIdx];
                    const active = sortConfig?.key === col;
                    const direction = active ? sortConfig!.direction : "asc";
                    return (
                      <TableCell key={col}>
                        <Box
                          onClick={() => handleSort(col)}
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 1,
                            cursor: "pointer",
                            userSelect: "none",
                          }}
                        >
                          <Box component="span">{col}</Box>
                          <Box
                            component="span"
                            sx={{
                              fontSize: 16,
                              lineHeight: 1,
                              color: active ? "primary.main" : "text.secondary",
                            }}
                          >
                            {active ? (direction === "asc" ? "▲" : "▼") : "◄►"}
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  })}
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedRows.map((r, rowIdx) => (
                  <TableRow key={rowIdx} hover>
                    {visibleColumnIndexes.map((colIdx) => (
                      <TableCell key={`${rowIdx}-${colIdx}`}>
                        {r[colIdx] ?? ""}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {hasTable && (
        <Box ref={paginationRef}>
          <TablePagination
            component="div"
            count={filteredRowsCount}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Rows per page:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
            }
            sx={{
              borderTop: "1px solid",
              borderColor: "divider",
              mt: 1,
            }}
          />
        </Box>
      )}
    </Box>
  );
};
